import { DataListItem, DataListItemRow, DataListItemCells, DataListCell, Stack, StackItem, Toolbar, ToolbarContent, ToolbarItem, Button, Form, DataList, Divider } from '@patternfly/react-core';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@patternfly/react-icons';

import { ModelSelectionUndoState } from './ModelSelectionPage';
import CustomizationLabel from '../../components/customization-label';
import { Modality, ModalityDirection, Model, ModelType } from '../../store/global-types/customization-types';
import ModalitiesAndTags from '../../components/ModalitiesAndTags';

interface SelectionRulesFormProps {
    model?: Model,
    modelType: ModelType,
    aeTitleRuleIds: string[],
    dicomRuleIds: string[],
    modelName: string,
    modelLabel: string,
    customizationName: string,
    isCustomizationBaseModified: boolean,
    onAeTitleRuleAdded: () => void,
    onDicomRuleAdded: () => void,
    setUndoState: (undoState: ModelSelectionUndoState) => void,
    heading: string,
    hideCustomizationName?: boolean,
    showMultipleOutputsWarning: boolean,
    /** If true, don't show undo UI options. */
    isUndoDisabled?: boolean,
    getAeTitleRule: (id: string, runningNumber: number, isLastRule: boolean) => JSX.Element,
    getDicomRule: (id: string, runningNumber: number, isLastRule: boolean) => JSX.Element,
    isDicomSelectionDisabled?: boolean,
    onModelTagToggled?: (tag: string) => void,
    onModelVersionTagToggled?: (tag: string) => void,
    onModelModalityToggled?: (modality: Modality, direction: ModalityDirection) => void,
    activeTags?: string[],
    activeVersionTags?: string[],
    activeInputModalities?: Modality[],
    activeOutputModalities?: Modality[],
}

const supportedModelTypes = [ModelType.Contouring, ModelType.Dose, ModelType.Image, ModelType.Adapt];

const SelectionRulesForm = (props: SelectionRulesFormProps) => {
    const { model, modelType, aeTitleRuleIds, dicomRuleIds, modelName, modelLabel, customizationName,
        onAeTitleRuleAdded, onDicomRuleAdded,
        isCustomizationBaseModified, heading, hideCustomizationName, showMultipleOutputsWarning,
        getAeTitleRule, getDicomRule, isDicomSelectionDisabled,
        onModelTagToggled, onModelVersionTagToggled, onModelModalityToggled,
        activeTags, activeVersionTags,
        activeInputModalities, activeOutputModalities } = props;

    const { t } = useTranslation();


    const handleAddAeTitleRuleClick = useCallback(() => {
        onAeTitleRuleAdded();
    }, [onAeTitleRuleAdded]);

    const handleAddDicomRuleClick = useCallback(() => {
        onDicomRuleAdded();
    }, [onDicomRuleAdded]);

    if (!supportedModelTypes.includes(modelType)) {
        throw new Error(`Unsupported model type: ${modelType}`);
    }


    const dataListCells = [
        <DataListCell key="modelRules" width={4}>
            <div>
                <Form>
                    <Stack>
                        {showMultipleOutputsWarning && (
                            <div className="multiple-outputs-warning">
                                Note: This customization contains multiple target files. Adjusting the selection triggers here will affect the entire&nbsp;
                                {/* eslint-disable-next-line react/no-unescaped-entities */}
                                customization <span className="multiple-outputs-base">"{modelName}.{customizationName}"</span>, not just this target file.
                            </div>
                        )}
                        <div>
                            {aeTitleRuleIds.map((aeTitleRuleId, i, rules) => (getAeTitleRule(aeTitleRuleId, i + 1, i === rules.length - 1)))}
                        </div>
                        {aeTitleRuleIds.length > 0 && dicomRuleIds.length > 0 && <Divider className="selection-divider" />}
                        <div>
                            {dicomRuleIds.map((dicomRuleId, i, rules) => (getDicomRule(dicomRuleId, i + 1, i === rules.length - 1)))}
                        </div>

                        <Divider />

                        <StackItem className="new-rule-toolbar model-selection-toolbar">
                            <Toolbar>
                                <ToolbarContent>
                                    {!isDicomSelectionDisabled &&
                                        <ToolbarItem>
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                icon={<PlusIcon />}
                                                // isDisabled={isToolbarDisabled}
                                                data-testid="add-dicom-rule-button"
                                                onClick={handleAddDicomRuleClick}>{t('selectionPage.addDicomRule')}</Button>
                                        </ToolbarItem>}
                                    <ToolbarItem>
                                        <Button
                                            size="sm"
                                            variant="tertiary"
                                            style={{ color: 'white' }}
                                            icon={<PlusIcon />}
                                            // isDisabled={isToolbarDisabled}
                                            className="save-changes-button"
                                            data-testid="add-ae-title-rule-button"
                                            onClick={handleAddAeTitleRuleClick}>{t('selectionPage.addAeTitleRule')}</Button>
                                    </ToolbarItem>
                                </ToolbarContent>
                            </Toolbar>
                        </StackItem>
                    </Stack>
                </Form>
            </div>
        </DataListCell>
    ];

    if (!hideCustomizationName) {
        dataListCells.unshift(
            <DataListCell key="modelName" width={1}>
                <div className="selection-customization-label">
                    <CustomizationLabel friendlyName={modelLabel} modelName={modelName} customizationName={customizationName} isModified={isCustomizationBaseModified} />
                </div>
                {model && (
                    <div className="selection-modalities-and-tags">
                        <ModalitiesAndTags
                            model={model}
                            onModelModalityToggled={onModelModalityToggled}
                            onModelTagToggled={onModelTagToggled}
                            onModelVersionTagToggled={onModelVersionTagToggled}
                            activeTags={activeTags}
                            activeVersionTags={activeVersionTags}
                            activeInputModalities={activeInputModalities}
                            activeOutputModalities={activeOutputModalities}
                        />
                    </div>
                )}
            </DataListCell>
        );
    }

    return (
        <DataList aria-label={`Model selection configuration`} isCompact>
            <div id={heading} className="customization-rule-jump-heading"></div>
            <DataListItem aria-labelledby={`Model selection rules for customization ${customizationName}`}>
                <DataListItemRow className="model-selection-model-rules">
                    <DataListItemCells dataListCells={dataListCells} />
                </DataListItemRow>
            </DataListItem>
        </DataList>
    );
}

export default SelectionRulesForm;
